import {
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { X } from '@phosphor-icons/react';
import { ChangeEvent } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { ICreateBrand } from '../../../schemas/apis/interface';

interface BrandDrawerProps {
  open: boolean;
  onClose: () => void;
  brandData: ICreateBrand;
  setBrandData: (data: ICreateBrand) => void;
  onSave: (
    e: React.MouseEvent<HTMLButtonElement> | React.FormEvent
  ) => Promise<void>;
  sameAsBusinessContact: boolean;
  setSameAsBusinessContact: (value: boolean) => void;
  handleCheckboxChange: (event: ChangeEvent<HTMLInputElement>) => void;
  isFormSubmitted: boolean;
  isPending: boolean;
  validateEmail: (email: string) => boolean;
}

const BrandDrawer = ({
  open,
  onClose,
  brandData,
  setBrandData,
  onSave,
  sameAsBusinessContact,
  handleCheckboxChange,
  isFormSubmitted,
  isPending,
  validateEmail,
}: BrandDrawerProps) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: '420px',
          padding: '14px 18px',
          backgroundColor: '#FAFBFD',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        {/* Close (Cancel) Button */}
        <Box sx={{ gap: 1.5, display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              '&:hover': { backgroundColor: '#E8EBF1' },
            }}
          >
            <X size={18} />
          </IconButton>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 16,
              color: 'custom.textBlue',
              letterSpacing: '-0.2px',
            }}
          >
            {brandData.id ? `Edit Brand: ${brandData.name}` : 'Add Brand'}
          </Typography>
        </Box>

        {/* Save Button */}
        <Button
          size="small"
          variant="contained"
          disabled={isPending || brandData.name === ''}
          sx={{
            fontSize: 14,
            fontWeight: 500,
            borderRadius: '6px',
            textTransform: 'none',
            boxShadow: 'none',
            px: 3,
            py: 0.75,
            backgroundColor: 'primary.main',
          }}
          onClick={onSave}
        >
          {isPending ? 'Saving...' : 'Save'}
        </Button>
      </Box>

      <Divider
        sx={{
          borderColor: 'rgba(0, 0, 0, 0.06)',
          mb: 1,
          mx: -3,
        }}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            sx={{
              minWidth: 'fit-content',
              fontWeight: 500,
              fontSize: 14,
              color: 'lightGray.text',
            }}
          >
            Brand Name
          </Typography>
          <TextField
            name="name"
            placeholder="Enter brand name"
            value={brandData.name || ''}
            onChange={e => {
              setBrandData({ ...brandData, name: e.target.value });
            }}
            fullWidth
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '40px',
                borderRadius: '6px',
                backgroundColor: 'white',
                '&:hover': {
                  '& fieldset': { borderColor: '#D0D5DD' },
                },
                '&.Mui-focused': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '1px',
                  },
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '10px 14px',
                fontSize: '14px',
              },
            }}
          />
        </Box>

        <Divider
          sx={{
            borderColor: 'rgba(0, 0, 0, 0.06)',
            mx: -3,
          }}
        />

        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ my: 1 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 600,
                color: 'custom.textBlue',
                mb: 0.5,
              }}
            >
              Brand Contact Details
            </Typography>
            <Typography
              sx={{
                fontSize: 13,
                color: 'text.secondary',
              }}
            >
              Same as Business Contact
            </Typography>
          </Box>
          <Checkbox
            checked={sameAsBusinessContact}
            onChange={handleCheckboxChange}
            sx={{
              color: 'primary.light',
              '&.Mui-checked': {
                color: 'primary.main',
              },
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            sx={{
              minWidth: '50px',
              fontWeight: 500,
              fontSize: 14,
              color: 'lightGray.text',
            }}
          >
            Name
          </Typography>
          <TextField
            name="poc_at_brand"
            placeholder="Arin"
            value={brandData.poc_at_brand || ''}
            onChange={e =>
              setBrandData({
                ...brandData,
                poc_at_brand: e.target.value,
              })
            }
            disabled={sameAsBusinessContact}
            fullWidth
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '40px',
                borderRadius: '6px',
                backgroundColor: sameAsBusinessContact ? '#f5f5f5' : 'white',
                transition: 'all 0.2s ease',
                '&:hover': {
                  '& fieldset': { borderColor: '#D0D5DD' },
                },
                '&.Mui-focused': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '1px',
                  },
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '10px 14px',
                fontSize: '14px',
              },
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            sx={{
              minWidth: '50px',
              fontWeight: 500,
              fontSize: 14,
              color: 'lightGray.text',
            }}
          >
            Email
          </Typography>
          <TextField
            name="poc_at_email"
            placeholder="example@gmail.com"
            value={brandData.poc_at_email || ''}
            onChange={e =>
              setBrandData({
                ...brandData,
                poc_at_email: e.target.value,
              })
            }
            disabled={sameAsBusinessContact}
            fullWidth
            variant="outlined"
            error={
              isFormSubmitted &&
              brandData.poc_at_email !== '' &&
              !validateEmail(brandData.poc_at_email || '')
            }
            helperText={
              isFormSubmitted &&
              brandData.poc_at_email !== '' &&
              !validateEmail(brandData.poc_at_email || '')
                ? 'Invalid email format'
                : ''
            }
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '40px',
                borderRadius: '6px',
                backgroundColor: sameAsBusinessContact ? '#f5f5f5' : 'white',
                transition: 'all 0.2s ease',
                '&:hover': {
                  '& fieldset': { borderColor: '#D0D5DD' },
                },
                '&.Mui-focused': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '1px',
                  },
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '10px 14px',
                fontSize: '14px',
              },
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            sx={{
              minWidth: '50px',
              fontWeight: 500,
              fontSize: 14,
              color: 'lightGray.text',
            }}
          >
            Role
          </Typography>
          <TextField
            name="poc_role"
            placeholder="Manager"
            value={brandData.poc_role || ''}
            onChange={e =>
              setBrandData({
                ...brandData,
                poc_role: e.target.value,
              })
            }
            disabled={sameAsBusinessContact}
            fullWidth
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '40px',
                borderRadius: '6px',
                backgroundColor: sameAsBusinessContact ? '#f5f5f5' : '#F8F9FC',
                transition: 'all 0.2s ease',
                '&:hover': {
                  '& fieldset': { borderColor: '#D0D5DD' },
                },
                '&.Mui-focused': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '1px',
                  },
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '10px 14px',
                fontSize: '14px',
              },
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            sx={{
              minWidth: '50px',
              fontWeight: 500,
              fontSize: 14,
              color: 'lightGray.text',
            }}
          >
            Contact
          </Typography>
          <Box sx={{ width: '100%' }}>
            <PhoneInput
              country="us"
              value={brandData.poc_contact || ''}
              onChange={phone => {
                setBrandData({
                  ...brandData,
                  poc_contact: phone,
                });
              }}
              disabled={sameAsBusinessContact}
              inputStyle={{
                width: '100%',
                height: '40px',
                fontSize: '14px',
                backgroundColor: sameAsBusinessContact ? '#f5f5f5' : '#F8F9FC',
                borderRadius: '6px',
                border:
                  isFormSubmitted &&
                  brandData.poc_contact &&
                  brandData.poc_contact.length > 0 &&
                  brandData.poc_contact.length < 10
                    ? '1px solid #d32f2f'
                    : '1px solid rgba(0, 0, 0, 0.12)',
                transition: 'all 0.2s ease',
              }}
              inputProps={{ name: 'phone', required: true }}
              containerStyle={{
                width: '100%',
                position: 'relative',
              }}
              dropdownStyle={{
                position: 'absolute',
                top: 'auto',
                bottom: '100%',
                left: 0,
                zIndex: 9999,
                boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
                borderRadius: '6px',
              }}
              placeholder="Placeholder"
              specialLabel=""
              enableSearch={true}
            />
            {isFormSubmitted &&
              brandData.poc_contact &&
              brandData.poc_contact.length > 0 &&
              brandData.poc_contact.length < 10 && (
                <Typography
                  sx={{
                    color: '#d32f2f',
                    fontSize: '0.75rem',
                    mt: 0.5,
                    ml: 1.5,
                  }}
                >
                  Phone number must be at least 10 digits
                </Typography>
              )}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default BrandDrawer;
